import { useCallback, useEffect, useRef, useState } from "react";
import { api, type ChatMessage, type ChatConversation } from "@/lib/api";
import { useAuth } from "./useAuth";

export type ChatState = {
	conversation: ChatConversation | null;
	messages: ChatMessage[];
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	nextCursor: string | null;
	unreadCount: number;
	firstUnreadIndex: number;
};

export function useChat() {
	const { getToken, isSignedIn } = useAuth();
	const getTokenRef = useRef(getToken);
	useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

	const [state, setState] = useState<ChatState>({
		conversation: null,
		messages: [],
		loading: false,
		loadingMore: false,
		error: null,
		nextCursor: null,
		unreadCount: 0,
		firstUnreadIndex: -1,
	});

	const loadConversation = useCallback(async () => {
		if (!isSignedIn) return null;
		const token = await getTokenRef.current();
		if (!token) return null;
		try {
			const conv = await api.chatConversation(token);
			setState(s => ({ ...s, conversation: conv }));
			return conv;
		} catch (err) {
			console.error("Failed to load conversation:", err);
			return null;
		}
	}, [isSignedIn]);

	const loadMessages = useCallback(async (conversationId: string, cursor?: string) => {
		const token = await getTokenRef.current();
		if (!token) return;
		if (cursor) {
			setState(s => ({ ...s, loadingMore: true }));
		} else {
			setState(s => ({ ...s, loading: true, error: null }));
		}
		try {
			const data = await api.chatMessages(token, conversationId, cursor);
			const reversed = [...data.messages].reverse();
			setState(s => {
				const msgs = cursor ? [...reversed, ...s.messages] : reversed;
				const idx = s.unreadCount > 0 && !cursor && msgs.length > 0
					? msgs.length - s.unreadCount
					: s.firstUnreadIndex;
				return {
					...s,
					messages: msgs,
					nextCursor: data.nextCursor,
					loading: false,
					loadingMore: false,
					firstUnreadIndex: idx,
				};
			});
		} catch (err) {
			setState(s => ({
				...s,
				loading: false,
				loadingMore: false,
				error: err instanceof Error ? err.message : "Failed to load messages",
			}));
		}
	}, []);

	const pendingSendIds = useRef<Set<string>>(new Set());

	const sendMessage = useCallback(async (content: string, replyToId?: string, attachmentIds?: string[]) => {
		const token = await getTokenRef.current();
		if (!token) return null;
		try {
			const msg = await api.chatSend(token, { content, replyToId, attachmentIds });
			pendingSendIds.current.add(msg.id);
			setTimeout(() => pendingSendIds.current.delete(msg.id), 5000);
			setState(s => ({
				...s,
				messages: [...s.messages, msg],
			}));
			return msg;
		} catch (err) {
			console.error("Send failed:", err);
			return null;
		}
	}, []);

	const addRealtimeMessage = useCallback((msg: ChatMessage) => {
		if (pendingSendIds.current.has(msg.id)) return;
		setState(s => {
			if (s.messages.some(m => m.id === msg.id)) return s;
			return { ...s, messages: [...s.messages, msg] };
		});
	}, []);

	const markRead = useCallback(async (conversationId: string, lastMessageId: string) => {
		const token = await getTokenRef.current();
		if (!token) return;
		try {
			await api.chatMarkRead(token, conversationId, lastMessageId);
			setState(s => ({ ...s, unreadCount: 0, firstUnreadIndex: -1 }));
		} catch {
			// mark-read is best-effort
		}
	}, []);

	const editMessage = useCallback(async (messageId: string, content: string) => {
		const token = await getTokenRef.current();
		if (!token) return null;
		try {
			const edited = await api.chatEdit(token, messageId, content);
			setState(s => ({
				...s,
				messages: s.messages.map(m => m.id === messageId ? { ...m, content: edited?.content ?? content, editedAt: edited?.editedAt ?? Date.now() } : m),
			}));
			return edited;
		} catch (err) {
			console.error("Edit failed:", err);
			return null;
		}
	}, []);

	const deleteMessage = useCallback(async (messageId: string) => {
		const token = await getTokenRef.current();
		if (!token) return;
		try {
			await api.chatDelete(token, messageId);
			setState(s => ({
				...s,
				messages: s.messages.map(m => m.id === messageId ? { ...m, deletedAt: Date.now() } : m),
			}));
		} catch (err) {
			console.error("Delete failed:", err);
		}
	}, []);

	const loadUnread = useCallback(async () => {
		const token = await getTokenRef.current();
		if (!token) return;
		try {
			const data = await api.chatUnread(token);
			setState(s => {
				const idx = data.unreadCount > 0 && s.messages.length > 0
					? s.messages.length - data.unreadCount
					: -1;
				return { ...s, unreadCount: data.unreadCount, firstUnreadIndex: idx };
			});
		} catch {
			// unread count is best-effort
		}
	}, []);

	useEffect(() => {
		if (!isSignedIn) {
			setState({ conversation: null, messages: [], loading: false, loadingMore: false, error: null, nextCursor: null, unreadCount: 0, firstUnreadIndex: -1 });
			return;
		}
		let cancelled = false;
		(async () => {
			const conv = await loadConversation();
			if (cancelled || !conv) return;
			const token = await getTokenRef.current();
			if (!token) return;
			const data = await api.chatMessages(token, conv.conversationId);
			if (cancelled) return;
			const reversed = [...data.messages].reverse();
			const unread = await api.chatUnread(token);
			if (cancelled) return;
			const idx = unread.unreadCount > 0 && reversed.length > 0
				? reversed.length - unread.unreadCount
				: -1;
			setState(s => ({
				...s,
				messages: reversed,
				nextCursor: data.nextCursor,
				loading: false,
				loadingMore: false,
				unreadCount: unread.unreadCount,
				firstUnreadIndex: idx,
			}));
			if (reversed.length > 0) {
				const lastMsg = reversed[reversed.length - 1];
				markRead(conv.conversationId, lastMsg.id);
			}
		})();
		return () => { cancelled = true; };
	}, [isSignedIn, loadConversation, markRead]);

	return {
		...state,
		loadMessages,
		loadMore: useCallback(() => {
			if (state.conversation && state.nextCursor && !state.loadingMore) {
				loadMessages(state.conversation.conversationId, state.nextCursor);
			}
		}, [state.conversation, state.nextCursor, state.loadingMore, loadMessages]),
		sendMessage,
		editMessage,
		deleteMessage,
		addRealtimeMessage,
		markRead,
		loadUnread,
	};
}
